import { useState, useCallback, useRef } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn, ZoomOut, Play } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';

export const ImageGallery = ({ images = [], videos = [], title = '' }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isZoomOpen, setIsZoomOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [currentVideo, setCurrentVideo] = useState(null);
  const lastTouchDistance = useRef(null);
  const lastTouchCenter = useRef(null);

  // Swipe state for main gallery
  const swipeStart = useRef(null);
  const swipeThreshold = 50;

  const defaultImage = "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=1200&q=80";
  const displayImages = images.length > 0 ? images : [defaultImage];
  
  // Combine images and videos for display
  const allMedia = [
    ...displayImages.map(img => ({ type: 'image', src: img })),
    ...(videos || []).map(vid => ({ type: 'video', ...vid }))
  ];

  // Fix image URL if it's a relative path
  const getImageUrl = (src) => {
    if (src.startsWith('/api')) {
      return `${process.env.REACT_APP_BACKEND_URL}${src}`;
    }
    return src;
  };

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % allMedia.length);
  }, [allMedia.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + allMedia.length) % allMedia.length);
  }, [allMedia.length]);

  const openZoom = () => {
    const current = allMedia[currentIndex];
    if (current.type === 'video') {
      setCurrentVideo(current);
      setShowVideoModal(true);
    } else {
      setIsZoomOpen(true);
      setZoomLevel(1);
      setPosition({ x: 0, y: 0 });
    }
  };

  const closeZoom = () => {
    setIsZoomOpen(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 4));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
    if (zoomLevel <= 1.5) {
      setPosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging && zoomLevel > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      handleZoomIn();
    } else {
      handleZoomOut();
    }
  };

  // Touch handlers for pinch-to-zoom and drag
  const getTouchDistance = (touches) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      lastTouchDistance.current = getTouchDistance(e.touches);
      lastTouchCenter.current = {
        x: (e.touches[0].clientX + e.touches[1].clientX) / 2,
        y: (e.touches[0].clientY + e.touches[1].clientY) / 2
      };
    } else if (e.touches.length === 1 && zoomLevel > 1) {
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX - position.x, y: e.touches[0].clientY - position.y });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = getTouchDistance(e.touches);
      if (lastTouchDistance.current) {
        const scale = dist / lastTouchDistance.current;
        setZoomLevel(prev => Math.min(Math.max(prev * scale, 1), 4));
      }
      lastTouchDistance.current = dist;
    } else if (e.touches.length === 1 && isDragging && zoomLevel > 1) {
      e.preventDefault();
      setPosition({
        x: e.touches[0].clientX - dragStart.x,
        y: e.touches[0].clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      lastTouchDistance.current = null;
      lastTouchCenter.current = null;
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
      if (zoomLevel <= 1) {
        setPosition({ x: 0, y: 0 });
      }
    }
  };

  // Swipe handlers for main gallery (distinguish swipe vs tap)
  const handleGalleryTouchStart = (e) => {
    if (e.touches.length === 1) {
      swipeStart.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, time: Date.now() };
    }
  };

  const handleGalleryTouchEnd = (e) => {
    if (!swipeStart.current) return;
    const touch = e.changedTouches[0];
    const dx = touch.clientX - swipeStart.current.x;
    const dy = touch.clientY - swipeStart.current.y;
    const elapsed = Date.now() - swipeStart.current.time;
    swipeStart.current = null;

    // If horizontal swipe is significant and mostly horizontal
    if (Math.abs(dx) > swipeThreshold && Math.abs(dx) > Math.abs(dy) * 1.5 && elapsed < 500) {
      if (dx < 0) {
        goToNext();
      } else {
        goToPrev();
      }
      return;
    }

    // Short tap = open zoom (only if not a swipe)
    if (Math.abs(dx) < 10 && Math.abs(dy) < 10 && elapsed < 300) {
      openZoom();
    }
  };

  const currentMedia = allMedia[currentIndex];

  return (
    <div className="space-y-4" data-testid="image-gallery">
      {/* Main Display */}
      <div
        className="gallery-main relative cursor-pointer group"
        onTouchStart={handleGalleryTouchStart}
        onTouchEnd={handleGalleryTouchEnd}
        onClick={(e) => { if (window.matchMedia('(pointer: fine)').matches) openZoom(); }}
      >
        {currentMedia.type === 'video' ? (
          <>
            {currentMedia.tipo === 'youtube' ? (
              <img
                src={currentMedia.thumbnail || `https://img.youtube.com/vi/${currentMedia.url.split('/').pop()}/maxresdefault.jpg`}
                alt={`${title} - Video`}
                className="w-full h-full object-cover"
              />
            ) : (
              <video
                src={getImageUrl(currentMedia.url)}
                className="w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 flex items-center justify-center bg-black/30">
              <div className="w-20 h-20 rounded-full bg-primary/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                <Play className="w-10 h-10 text-white ml-1" />
              </div>
            </div>
          </>
        ) : (
          <>
            <img
              src={getImageUrl(currentMedia.src)}
              alt={`${title} - Imagen ${currentIndex + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
            
            {/* Zoom Icon */}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
              <ZoomIn className="w-12 h-12 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          </>
        )}

        {/* Navigation Arrows */}
        {allMedia.length > 1 && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={(e) => { e.stopPropagation(); goToPrev(); }}
              data-testid="gallery-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white rounded-full"
              onClick={(e) => { e.stopPropagation(); goToNext(); }}
              data-testid="gallery-next"
            >
              <ChevronRight className="w-6 h-6" />
            </Button>
          </>
        )}

        {/* Counter */}
        <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm font-medium px-3 py-1 rounded">
          {currentIndex + 1} / {allMedia.length}
        </div>
      </div>

      {/* Thumbnails */}
      {allMedia.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          {allMedia.map((media, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`gallery-thumbnail flex-shrink-0 relative ${
                index === currentIndex ? 'active' : ''
              }`}
              data-testid={`thumbnail-${index}`}
            >
              {media.type === 'video' ? (
                <>
                  <img
                    src={media.tipo === 'youtube' 
                      ? (media.thumbnail || `https://img.youtube.com/vi/${media.url.split('/').pop()}/hqdefault.jpg`)
                      : 'https://via.placeholder.com/80x60?text=Video'}
                    alt={`Video ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <Play className="w-4 h-4 text-white" />
                  </div>
                </>
              ) : (
                <img
                  src={getImageUrl(media.src)}
                  alt={`${title} - Miniatura ${index + 1}`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = defaultImage;
                  }}
                />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Image Zoom Modal */}
      <Dialog open={isZoomOpen} onOpenChange={setIsZoomOpen}>
        <DialogContent
          className="!fixed !inset-0 !max-w-none !w-screen !h-screen !m-0 !p-0 !bg-black/95 !border-none !overflow-hidden !rounded-none !translate-x-0 !translate-y-0 !top-0 !left-0 [&>button:last-child]:hidden"
          onPointerDownOutside={closeZoom}
        >
          {/* Controls */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomIn}
              className="bg-white/20 hover:bg-white/30 text-white rounded-full h-10 w-10"
              data-testid="zoom-in"
            >
              <ZoomIn className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleZoomOut}
              className="bg-white/20 hover:bg-white/30 text-white rounded-full h-10 w-10"
              data-testid="zoom-out"
            >
              <ZoomOut className="w-5 h-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={closeZoom}
              className="bg-white/20 hover:bg-white/30 text-white rounded-full h-10 w-10"
              data-testid="zoom-close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Zoom Level Indicator */}
          <div className="absolute top-4 left-4 z-10 bg-white/10 text-white text-sm px-3 py-1 rounded">
            {Math.round(zoomLevel * 100)}%
          </div>

          {/* Navigation */}
          {allMedia.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full z-10 h-10 w-10"
                onClick={goToPrev}
              >
                <ChevronLeft className="w-6 h-6" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 text-white rounded-full z-10 h-10 w-10"
                onClick={goToNext}
              >
                <ChevronRight className="w-6 h-6" />
              </Button>
            </>
          )}

          {/* Zoomable Image */}
          <div
            className="w-full h-full flex items-center justify-center overflow-hidden touch-none"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            style={{ cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
          >
            <img
              src={getImageUrl(currentMedia.type === 'image' ? currentMedia.src : defaultImage)}
              alt={`${title} - Zoom`}
              className="max-w-full max-h-full object-contain select-none"
              style={{
                transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                transition: isDragging ? 'none' : 'transform 0.2s ease-out'
              }}
              draggable={false}
              onError={(e) => {
                e.target.src = defaultImage;
              }}
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Video Modal */}
      <Dialog open={showVideoModal} onOpenChange={setShowVideoModal}>
        <DialogContent className="max-w-4xl w-full p-0 bg-black border-none overflow-hidden">
          <div className="absolute top-4 right-4 z-10">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowVideoModal(false)}
              className="bg-white/10 hover:bg-white/20 text-white rounded-full"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
          
          <div className="aspect-video w-full">
            {currentVideo?.tipo === 'youtube' ? (
              <iframe
                src={currentVideo.url}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Video de YouTube"
              />
            ) : currentVideo ? (
              <video
                src={getImageUrl(currentVideo.url)}
                className="w-full h-full"
                controls
                autoPlay
              />
            ) : null}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
